import { Flex, Input, Textarea, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  AutoUpdateWrapper,
  useAutoUpdateForm,
} from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import { AutoGrowTextareaContainer } from '@texas/components/shared/form/AutoGrowTextareaContainer';
import {
  AutoUpdateInfoRequest,
  VariantInfo,
  variantsApi,
} from '@texas/api/endpoints/variantsApi';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { useActiveSession } from '@texas/hooks/useSession';
import { Role } from '@texas/api/endpoints/userApi';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { reactEvents } from 'src/bridge/reactEvents';
import { AutoUpdateResponseCallback } from '@texas/components/shared/autoUpdate/types';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { useEffect } from 'react';

export function GeneralInfoForm({ variantInfo }: { variantInfo: VariantInfo }) {
  const { t } = useTranslation();
  const session = useActiveSession();
  const disabled = !hasRole(session.currentUser.role, [
    Role.InternalAdministrator,
    Role.InternalAgent,
    Role.SystemAdministrator,
  ]);

  function onResponse(
    data: AutoUpdateResponseCallback<VariantInfo, AutoUpdateInfoRequest>,
  ) {
    if (data.states.filter((f) => f.hasError).length > 0) return;

    reactEvents.variantNameChanged.dispatch({
      id: variantInfo.id,
      value: data.unmodifiedServerData.name,
    });
  }

  const {
    registerAutoUpdate,
    form,
    formState,
    watch,
    triggerSubmit,
    register,
    setValue,
  } = useAutoUpdateForm<AutoUpdateInfoRequest, VariantInfo>({
    defaultValue: {
      name: variantInfo.name,
      notes: variantInfo.notes,
    },
    requestParams: [variantInfo.id],
    namePrefix: 'variantInfo',
    autoUpdateRequest: variantsApi.autoUpdateInfo,
    onResponse,
  });

  useEffect(() => {
    const unsub = reactEvents.variantNameChanged.subscribe((variant) => {
      if (variant.id === variantInfo.id) {
        setValue('name', variant.value);
      }
    });
    return () => unsub();
  }, [variantInfo.id, setValue]);

  return (
    <form {...form}>
      <Flex direction="column" gap={4}>
        <Text variant="header">{t('general.info')}</Text>
        {variantInfo.isProductDevelopment && (
          <Text variant="small">{t('variant.isPdNote')}</Text>
        )}
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="name"
          boxProps={{ flexGrow: '1' }}
        >
          <TexasFormLabel>{t('general.name')}</TexasFormLabel>
          <Input
            {...register('name', {
              required: true,
              minLength: {
                value: 1,
                message: t('errors.minLength', { count: 1 }),
              },
              maxLength: {
                value: 100,
                message: t('errors.maxLength', { count: 100 }),
              },
              onBlur: triggerSubmit,
            })}
            disabled={
              disabled ||
              variantInfo.isProductDevelopment ||
              variantInfo.optionLinkId !== null
            }
            p={1}
            fontSize="md"
            variant="texas"
          />
          {variantInfo.optionLinkId && (
            <Text variant="small">{t('variant.nameDisclaimer')}</Text>
          )}
          <ErrorLabel text={formState.errors.name?.message} />
        </AutoUpdateWrapper>
        <TexasInputWrapper
          error={formState.errors.notes}
          label={t('general.notes')}
        >
          <AutoUpdateWrapper
            autoUpdateProps={registerAutoUpdate}
            path="notes"
            boxProps={{ flexGrow: '1' }}
          >
            <AutoGrowTextareaContainer value={watch('notes')}>
              <Textarea
                {...register('notes', {
                  maxLength: {
                    value: 4000,
                    message: t('errors.maxLength', { count: 4000 }),
                  },
                  onBlur: triggerSubmit,
                })}
                disabled={disabled}
                minHeight={40}
                height="auto"
                variant="unstyled"
                padding={1}
                placeholder={t('general.notes')}
                width="100%"
              />
            </AutoGrowTextareaContainer>
          </AutoUpdateWrapper>
        </TexasInputWrapper>
      </Flex>
    </form>
  );
}
